import { useMutation, useQueryClient } from '@tanstack/react-query';

import { memberService } from '@/services/member-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { memberMutationKeys, memberQueryKeys } from './query-keys';

/**
 * Hook to revoke an invitation to a tenant.
 * Returns a react-query mutation that accepts an invitation ID.
 * The backend returns 404 when an invitation is successfully revoked (deleted).
 */
export function useRevokeInvitation(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: memberMutationKeys.revokeInvitation(tenantId),
    mutationFn: async (invitationId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      await memberService.revokeInvitation(tenantId, invitationId);

      await pollForResource(async () => {
        const invitation = await memberService.findInvitation(
          tenantId,
          invitationId
        );

        if (!invitation) {
          return { invitationId, status: 'revoked' };
        }

        if (invitation.status === 'revoked') {
          return invitation;
        }

        return null;
      });

      return true;
    },
    onSuccess: async () => {
      // Invalidate invitations for this tenant so lists refresh
      await qc.invalidateQueries({
        queryKey: memberQueryKeys.invitationListByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
